import styles from './selectorTable.module.css'
import { useEffect, useMemo, useState } from 'react'
import { SelectField } from '../fields/select-field/selectField'
import { Table } from '../table/table'
import { Action, Column, Option } from '../types'
import { Data, Data2 } from '../../../data-types'
import { Section } from '../section/section'

type Props = {
    rowId: string
    rowsData?: Data2[]
    rowData: (id: string) => Promise<Data2>
    columns: Column[]
    options: Option[]
    placeholder?: string
    readOnly?: boolean
    renderOption?: (option: Option) => Promise<JSX.Element>
    onChange?: (items: Data2[]) => void
}

export const SelectorTable = (props: Props) => {
    const {
        rowId,
        rowsData,
        rowData,
        columns,
        options,
        placeholder,
        readOnly,
        renderOption,
        onChange
    } = props

    const [rows, setRows] = useState<Data2[]>(rowsData || [])
    const [selectedValue, setSelectedValue] = useState<string | undefined>()

    useEffect(() => {
        setRows(rowsData || [])
    }, [rowsData])

    const selectedItemIds = useMemo(() => {
        return new Set(rows.map(row => row[rowId]?.toString()))
    }, [rows, rowId])

    const availableOptions = useMemo(() => {
        return options.filter(option => !selectedItemIds.has(option.value))
    }, [options, selectedItemIds])

    const handleSelect = async (value?: string) => {
        if (!value) return
        const newRow = await rowData(value)
        const updatedRows = [newRow, ...rows]
        setRows(updatedRows)
        setSelectedValue(undefined)
        onChange?.(updatedRows)
    }

    const handleRemove = (rowIdParam?: string) => {
        if (!rowIdParam) return
        const updatedRows = rows.filter(row => row[rowId]?.toString() !== rowIdParam)
        setRows(updatedRows)
        onChange?.(updatedRows)
    }

    const actions = (rowIdParam?: string): Action[] => [
        {
            icon: 'trash-fill',
            kind: 'secondary',
            tooltip: 'Eliminar',
            small: true,
            onClick: () => handleRemove(rowIdParam)
        }
    ]

    return (
        <Section id="selector-table" space={12}>
            <SelectField
                key={rows.length}
                id="selector-table-select"
                options={availableOptions}
                value={selectedValue}
                placeholder={placeholder}
                disabled={availableOptions.length === 0 || readOnly}
                optional
                renderOption={renderOption}
                onChange={handleSelect}
            />
            <Table
                columns={columns}
                rows={rows as Data[]}
                rowId={rowId}
                actions={actions}
                readOnly
                className={styles.table}
            />
        </Section>
    )
}
